// Session-local, bounded blob cache. No Audio element is played by prefetching.
// Current-card requests jump ahead of queued background work.
export default class VocabularyAudioCache {
  constructor(fetchBlob, concurrency = 3) {
    this.fetchBlob = fetchBlob;
    this.concurrency = concurrency;
    this.entries = new Map();
    this.queue = [];
    this.active = 0;
    this.sessionId = null;
  }

  update(sessionId, descriptors = []) {
    if (this.sessionId !== sessionId) { this.clear(); this.sessionId = sessionId; }
    const targets = [...new Map(descriptors.filter(item => item?.cardId && item?.audioUrl).map(item => [item.cardId, item])).values()].slice(0, 10);
    const allowed = new Set(targets.map(item => item.cardId));
    for (const [cardId, entry] of this.entries) {
      if (!allowed.has(cardId)) { entry.controller.abort(); entry.reject(new DOMException('Audio window changed', 'AbortError')); this.entries.delete(cardId); }
    }
    for (const item of targets) {
      if (this.entries.has(item.cardId)) continue;
      const entry = { ...item, controller: new AbortController(), started: false };
      entry.promise = new Promise((resolve, reject) => { entry.resolve = resolve; entry.reject = reject; });
      // Background failures are silent; a foreground read can still handle them.
      entry.promise.catch(() => {});
      this.entries.set(item.cardId, entry);
    }
    this.queue = targets.map(item => this.entries.get(item.cardId)).filter(entry => !entry.started);
    this.drain();
  }

  get(sessionId, cardId) {
    const entry = this.sessionId === sessionId && this.entries.get(cardId);
    if (!entry) return null;
    if (!entry.started) { this.queue = [entry, ...this.queue.filter(item => item !== entry)]; this.drain(); }
    return entry.promise;
  }

  drain() {
    while (this.active < this.concurrency && this.queue.length) {
      const entry = this.queue.shift();
      if (entry.started || entry.controller.signal.aborted) continue;
      entry.started = true;
      this.active += 1;
      Promise.resolve().then(() => this.fetchBlob(entry.audioUrl, { signal: entry.controller.signal }))
        .then(entry.resolve, error => {
          // Do not retry denied/unavailable background audio on every action.
          // Foreground playback can use its existing explicit retry path.
          entry.reject(error);
        }).finally(() => { this.active -= 1; this.drain(); });
    }
  }

  clear() {
    this.queue = [];
    for (const entry of this.entries.values()) {
      entry.controller.abort();
      entry.reject(new DOMException('Audio cache closed', 'AbortError'));
    }
    this.entries.clear();
    this.sessionId = null;
  }
}
